import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { CartItem as OriginalCartItem } from '../api/services/cart.service';
import cartService from '../api/services/cart.service'; // 导入默认导出的cartService实例
import { useMockData } from '../config/env';
import { mergeNoEmpty } from '../utils/mergeUtils';
import { useAuth } from './AuthContext'; // 导入用户认证上下文
import { useTranslation } from 'react-i18next';
import { getSimpleProductName } from '../utils/simpleProductName';
import i18n from 'i18next';

// 定义价格层级接口
export interface PriceTier {
  min: number;
  max: number | null;
  price: number;
  originalPrice?: number;
}

// 扩展CartItem接口，添加额外的UI需要的属性
export interface ExtendedCartItem extends OriginalCartItem {
  id: string;           // item_id的字符串版本
  code: string;         // 等同于part_number
  partNumber: string;   // 等同于part_number
  image: string;        // 等同于image_url，但保证不为undefined
  category: string;     // 等同于product_type
  productId: number;    // 等同于product_id
  priceTiers: PriceTier[];
  selected: boolean;
  originalPrice?: number;
  type: 'machine' | 'accessory' | 'consumable' | 'spare_part';  // 支持所有产品类型
  specs?: {
    partNumber: string;
    productName: string;
  };
  price: number;        // 对应于unit_price
  product?: any;        // 原始产品对象，供前端展示适用机型等
  
  // 必选备件相关字段
  is_required?: boolean;
  parent_part_number?: string;
  
  // 备件完整字段信息
  name_zh?: string;
  name_en?: string;
  spec?: string;
  spec_imperial?: string;
  app_model?: string;
  app_sn?: string;
  is_consumable?: number; // 1=易损，2=非易损，3=隐藏
  unit?: string;
  status?: string;
  
  // 包装信息
  package_size_cm?: string | null;
  package_size_inch?: string | null;
  net_weight_kg?: number | null;
  net_weight_lbs?: number | null;
  gross_weight_kg?: number | null;
  gross_weight_lbs?: number | null;
  pcs_per_box?: number | string | null;
  pcs_per_pallet?: number | string | null;
  
  // 新增主层字段，便于购物车直接访问
  voltage?: string;
  frequency?: string;
  model?: string;
  
  // 定价和库存信息
  pricing?: any[];
  inventory?: any[];
  product_type: 'machine' | 'accessory' | 'spare_part' | 'consumable'; // 必需字段，与OriginalCartItem一致
}

// For backward compatibility, export ExtendedCartItem as CartItem for external use
export type CartItem = ExtendedCartItem;

// 将服务返回的CartItem转为UI使用的ExtendedCartItem
const mapServiceCartItemToUICartItem = (item: OriginalCartItem): ExtendedCartItem => {
  // 将product_type映射为type
  let type: 'machine' | 'accessory' | 'consumable' | 'spare_part';
  switch (item.product_type) {
    case 'machine':
      type = 'machine';
      break;
    case 'accessory':
      type = 'accessory';
      break;
    case 'consumable':
      type = 'consumable';
      break;
    case 'spare_part':
      type = 'spare_part';
      break;
    default:
      type = 'machine'; // 默认值
  }

  const getDisplayName = (): string => {
    const lang = i18n.language.startsWith('zh') ? 'zh' : 'en';
    // 直接使用统一工具，内部已包含回退链与智能检测
    // 将 item 与其 properties 合并，确保 name_zh/name_en 在顶层可见
    const merged = { ...item.properties, ...item } as any;
    return getSimpleProductName(merged, lang as 'zh' | 'en');
  };

  // 增强的图片获取逻辑
  const getDisplayImage = (): string => {
    const props = item.properties || {};
    
    // 图片获取优先级
    const imageUrl = props.image_url || 
                    item.image_url || 
                    props.image || 
                    (item as any).image || 
                    '';
                    
    // 如果有图片URL则返回，否则根据产品类型返回默认图片
    if (imageUrl && imageUrl.trim() !== '') {
      return imageUrl;
    }
    
    // 根据产品类型返回默认图片
    switch (item.product_type) {
      case 'consumable':
        return '/images/consumables/default.jpg';
      case 'spare_part':
        return '/images/spare-parts/default.jpg';
      case 'accessory':
        return '/images/accessories/default.jpg';
      case 'machine':
        return '/images/machines/default.jpg';
      default:
        return '/images/placeholder.png';
    }
  };

  const displayName = getDisplayName();
  const displayImage = getDisplayImage();

  console.log('[CartContext.mapServiceCartItemToUICartItem] 最终映射结果:', {
    product_type: item.product_type,
    part_number: item.part_number,
    displayName,
    displayImage,
    originalName: item.name
  });

  return {
    ...item, // 保留原始CartItem的所有属性
    id: item.item_id.toString(),
    code: item.part_number,
    partNumber: item.part_number,
    image: displayImage,
    category: item.product_type,
    productId: item.product_id,
    priceTiers: [], // 默认为空数组
    selected: false,
    type: type,
    specs: {
      partNumber: item.part_number,
      productName: displayName
    },
    price: item.unit_price, // 将unit_price映射为price
    
    // 使用计算得到的显示名称，确保name字段正确
    name: displayName,
    
    // 必选备件相关字段 (从properties中提取)
    is_required: item.properties?.is_required || false,
    parent_part_number: item.properties?.parent_part_number,
    
    // 备件完整字段信息 (优先主层字段，其次properties)
    name_zh: (item as any).name_zh || item.properties?.name_zh || displayName,
    name_en: (item as any).name_en || item.properties?.name_en || displayName,
    spec: (item as any).spec || item.properties?.spec || '',
    spec_imperial: (item as any).spec_imperial || item.properties?.spec_imperial || '',
    app_model: (item as any).app_model || item.properties?.app_model || '',
    app_sn: (item as any).app_sn || item.properties?.app_sn || '',
    is_consumable: (item as any).is_consumable ?? item.properties?.is_consumable ?? 0,
    unit: (item as any).unit || item.properties?.unit || 'pcs',
    status: (item as any).status || item.properties?.status || 'publish',
    
    // 包装信息
    package_size_cm: (item as any).package_size_cm || item.properties?.package_size_cm || null,
    package_size_inch: (item as any).package_size_inch || item.properties?.package_size_inch || null,
    net_weight_kg: (item as any).net_weight_kg || item.properties?.net_weight_kg || null,
    net_weight_lbs: (item as any).net_weight_lbs || item.properties?.net_weight_lbs || null,
    gross_weight_kg: (item as any).gross_weight_kg || item.properties?.gross_weight_kg || null,
    gross_weight_lbs: (item as any).gross_weight_lbs || item.properties?.gross_weight_lbs || null,
    pcs_per_box: (item as any).pcs_per_box || item.properties?.pcs_per_box || null,
    pcs_per_pallet: (item as any).pcs_per_pallet || item.properties?.pcs_per_pallet || null,
    
    // 新增主层字段，优先主层，其次properties
    voltage: (item as any).voltage || item.properties?.voltage || '',
    frequency: (item as any).frequency || item.properties?.frequency || '',
    model: (item as any).model || item.properties?.model || '',
    
    // 定价和库存信息
    pricing: (item as any).pricing || item.properties?.pricing || [],
    inventory: (item as any).inventory || item.properties?.inventory || [],
    product_type: item.product_type,
    product: item
  };
};

// 定义购物车上下文接口
export interface CartContextType {
  items: ExtendedCartItem[];
  addItem: (item: ExtendedCartItem) => void;
  removeItem: (id: string) => void;
  removeMultipleItems: (ids: string[]) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  clearInvalidItems: () => Promise<void>;
  totalPrice: number;
  itemCount: number;
  selectedItems: ExtendedCartItem[];
  selectedCount: number;
  selectedTotal: number;
  toggleItemSelection: (id: string, selected: boolean) => void;
  selectAll: (selected: boolean) => void;
  isItemSelected: (id: string) => boolean;
  loading: boolean;
  syncError: string | null;
}

// 创建购物车上下文并提供默认值
export const CartContext = createContext<CartContextType>({
  items: [],
  addItem: () => {},
  removeItem: () => {},
  removeMultipleItems: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  clearInvalidItems: async () => {},
  totalPrice: 0,
  itemCount: 0,
  selectedItems: [],
  selectedCount: 0,
  selectedTotal: 0,
  toggleItemSelection: () => {},
  selectAll: () => {},
  isItemSelected: () => false,
  loading: false,
  syncError: null
});

interface CartProviderProps {
  children: ReactNode;
}

// 上下文提供者组件
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<ExtendedCartItem[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);
  const [syncError, setSyncError] = useState<string | null>(null);
  
  // 获取用户认证状态
  const { user, isAuthenticated } = useAuth();
  
  // 本地 properties 缓存，part_number 为 key
  const cartPropertiesMap = React.useRef<Record<string, any>>({});
  
  // 用于跟踪当前用户ID，检测用户切换
  const currentUserIdRef = React.useRef<number | null>(null);
  
  // 从API加载购物车数据
  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await cartService.getCart();
      console.log('🛒 [CartContext.fetchCart] Raw response:', response);
      
      // 使用现有的映射函数转换CartItem
      let extendedItems: ExtendedCartItem[] = (response.items || []).map(mapServiceCartItemToUICartItem);
      
      // 合并本地 properties 字段，使用产品类型+料号作为复合key
      const enhancedItems = extendedItems.map(item => {
        if (item.part_number) {
          const cacheKey = `${item.product_type}_${item.part_number}`;
          const localProps = cartPropertiesMap.current[cacheKey];
          if (localProps) {
            // 只用非空值合并 properties
            item.properties = mergeNoEmpty(item.properties || {}, localProps || {});
            item.name = localProps.name || item.name;
            item.image_url = localProps.image_url || item.image_url;
          }
        }
        return item;
      });
      
      setItems(enhancedItems);
      console.log('🛒 [CartContext.fetchCart] Cart loaded with', enhancedItems.length, 'items:', enhancedItems);
    } catch (error) {
      console.error('❌ [CartContext.fetchCart] Error fetching cart:', error);
      setSyncError('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载购物车
  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      // 用户未认证时清空购物车
      setItems([]);
      setSelectedItemIds(new Set());
      cartPropertiesMap.current = {};
      setLoading(false);
    }
  }, [isAuthenticated]);

  // 监听用户切换，当用户ID变化时清空购物车
  useEffect(() => {
    const newUserId = user?.id || null;
    
    // 如果这不是第一次加载，且用户ID发生了变化
    if (currentUserIdRef.current !== null && currentUserIdRef.current !== newUserId) {
      console.log('🔄 [CartContext] User changed from', currentUserIdRef.current, 'to', newUserId, '- clearing cart');
      
      // 清空购物车状态
      setItems([]);
      setSelectedItemIds(new Set());
      cartPropertiesMap.current = {};
      setSyncError(null);
      
      // 如果新用户已认证，重新加载购物车
      if (isAuthenticated && newUserId) {
        fetchCart();
      }
    }
    
    // 更新当前用户ID引用
    currentUserIdRef.current = newUserId;
  }, [user?.id, isAuthenticated]);
  
  // 🚀 优化版本：减少数据处理，增加缓存
  const addItem = async (newItem: ExtendedCartItem) => {
    try {
      setLoading(true);
      console.log('🛒 [CartContext.addItem] Starting with newItem:', newItem);
      
      // 🚀 优化：简化properties处理，只保留核心字段
      const coreProperties = {
        // 基本产品信息
        productName: newItem.name,
        name: newItem.name,
        part_number: newItem.part_number,
        image_url: newItem.image_url,
        price: newItem.unit_price,
        product_type: newItem.product_type,
        
        // 产品类型特定字段（最小化）
        ...(newItem.product_type === 'consumable' && {
          material: (newItem.properties as any)?.material,
          app_model: (newItem.properties as any)?.app_model,
          specs: (newItem.properties as any)?.specs
        }),
        ...(newItem.product_type === 'spare_part' && {
          app_model: newItem.app_model,
          app_sn: newItem.app_sn,
          unit: newItem.unit,
          required_parts: (newItem.properties as any)?.required_parts,
          required_quantity: (newItem.properties as any)?.required_quantity
        }),
        
        // 保留原始产品对象用于tooltip
        product: (newItem.properties as any)?.product,
        id: newItem.product_id,
        productId: newItem.product_id
      };

      // 🚀 优化：最小化API请求数据
      const addToCartRequest = {
        product_type: newItem.product_type,
        product_id: newItem.product_id,
        part_number: newItem.part_number,
        quantity: newItem.quantity,
        properties: coreProperties
      };

      console.log('🛒 [CartContext.addItem] Optimized request:', addToCartRequest);
      
      // 🚀 调用购物车服务
      const result = await cartService.addToCart(addToCartRequest);
      console.log('🛒 [CartContext.addItem] Service result:', result);
      
      // 🚀 优化：使用轻量级缓存
      if (newItem.part_number) {
        const cacheKey = `${newItem.product_type}_${newItem.part_number}`;
        cartPropertiesMap.current[cacheKey] = coreProperties;
        console.log('🛒 [CartContext.addItem] Cached with key:', cacheKey);
      }
      
      // 🚀 优化：延迟刷新购物车，避免阻塞UI
      setTimeout(() => {
        fetchCart().catch(error => {
          console.error('🛒 [CartContext.addItem] Delayed cart refresh failed:', error);
        });
      }, 100);
      
    } catch (error) {
      console.error('❌ [CartContext.addItem] Error:', error);
      setSyncError('Failed to add item to cart');
      setLoading(false);
      throw error;
    } finally {
      // 🚀 快速释放loading状态
      setTimeout(() => setLoading(false), 50);
    }
  };
  
  // 从购物车移除商品
  const removeItem = async (itemId: string) => {
    console.log('🛒 [CartContext.removeItem] Removing item:', itemId);
    try {
      const numericId = parseInt(itemId);
      if (isNaN(numericId)) {
        console.error('🛒 [CartContext.removeItem] Invalid item ID:', itemId);
        return;
      }
      
      await cartService.removeCartItem(numericId);
      console.log('🛒 [CartContext.removeItem] Item removed successfully, refreshing cart');
      
      // 立即刷新购物车状态
      await refreshCart();
    } catch (error) {
      console.error('🛒 [CartContext.removeItem] Error removing item:', error);
      // 可以在这里添加用户提示
    }
  };

  // 批量删除购物车商品
  const removeMultipleItems = async (itemIds: string[]) => {
    console.log('🛒 [CartContext.removeMultipleItems] Removing items:', itemIds);
    try {
      setLoading(true);
      
      // 并行删除所有选中的商品
      const deletePromises = itemIds.map(async (itemId) => {
        const numericId = parseInt(itemId);
        if (isNaN(numericId)) {
          console.error('🛒 [CartContext.removeMultipleItems] Invalid item ID:', itemId);
          return { success: false, itemId, error: 'Invalid ID' };
        }
        
        try {
          await cartService.removeCartItem(numericId);
          return { success: true, itemId };
        } catch (error) {
          console.error('🛒 [CartContext.removeMultipleItems] Error removing item:', itemId, error);
          return { success: false, itemId, error: error.message };
        }
      });
      
      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;
      
      console.log(`🛒 [CartContext.removeMultipleItems] Completed: ${successCount} success, ${failureCount} failures`);
      
      // 清空选中状态
      setSelectedItemIds(new Set());
      
      // 立即刷新购物车状态
      await refreshCart();
      
      // 设置成功消息
      if (successCount > 0) {
        setSyncError(`成功删除 ${successCount} 个商品${failureCount > 0 ? `，${failureCount} 个失败` : ''}`);
        setTimeout(() => setSyncError(null), 3000);
      }
      
    } catch (error) {
      console.error('🛒 [CartContext.removeMultipleItems] Error in bulk remove:', error);
      setSyncError('批量删除商品时出错');
      setTimeout(() => setSyncError(null), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  // 更新购物车商品数量
  const updateQuantity = async (itemId: string, quantity: number) => {
    console.log('🛒 [CartContext.updateQuantity] Updating quantity for item:', itemId, 'to:', quantity);
    if (quantity <= 0) {
      await removeItem(itemId);
      return;
    }
    
    try {
      const numericId = parseInt(itemId);
      if (isNaN(numericId)) {
        console.error('🛒 [CartContext.updateQuantity] Invalid item ID:', itemId);
        return;
      }
      
      await cartService.updateCartItem(numericId, { quantity });
      console.log('🛒 [CartContext.updateQuantity] Quantity updated successfully, refreshing cart');
      
      // 立即刷新购物车状态
      await refreshCart();
    } catch (error) {
      console.error('🛒 [CartContext.updateQuantity] Error updating quantity:', error);
      // 可以在这里添加用户提示
    }
  };
  
  // 清空购物车
  const clearCart = async () => {
    console.log('🛒 [CartContext.clearCart] Clearing cart');
    try {
      await cartService.clearCart();
      console.log('🛒 [CartContext.clearCart] Cart cleared successfully, refreshing cart');
      
      // 立即刷新购物车状态
      await refreshCart();
    } catch (error) {
      console.error('🛒 [CartContext.clearCart] Error clearing cart:', error);
      // 可以在这里添加用户提示
    }
  };
  
  // 切换商品选中状态
  const toggleItemSelection = (id: string, selected: boolean) => {
    setSelectedItemIds(current => {
      const updated = new Set(current);
      if (selected) {
        updated.add(id);
      } else {
        updated.delete(id);
      }
      return updated;
    });
  };
  
  // 全选/取消全选
  const selectAll = (selected: boolean) => {
    if (selected) {
      const allIds = new Set(items.map(item => item.id.toString()));
      setSelectedItemIds(allIds);
    } else {
      setSelectedItemIds(new Set());
    }
  };
  
  // 判断商品是否被选中
  const isItemSelected = (id: string) => selectedItemIds.has(id);
  
  // 计算派生值
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const totalPrice = items.reduce((total, item) => total + (item.price * item.quantity), 0);
  
  const selectedItems = items.filter(item => selectedItemIds.has(item.id.toString()));
  const selectedCount = selectedItems.reduce((total, item) => total + item.quantity, 0);
  const selectedTotal = selectedItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  
  // 根据商品类型获取图片
  const getProductImage = (item: ExtendedCartItem): string => {
    if (item.image_url) return item.image_url;
    if (item.image) return item.image;
    
    // 根据产品类型返回默认图片
    switch (item.product_type) {
      case 'machine':
        return '/images/machines/default.jpg';
      case 'accessory':
        return '/images/accessories/default.jpg';
      case 'spare_part':
        return '/images/spare-parts/default.jpg';
      case 'consumable':
        return '/images/consumables/default.jpg';
      default:
        return '/images/placeholder.png';
    }
  };

  // 添加刷新购物车的方法
  const refreshCart = async () => {
    console.log('🛒 [CartContext.refreshCart] Refreshing cart data');
    try {
      const cartData = await cartService.getCart();
      // 使用现有的映射函数转换CartItem
      let extendedItems: ExtendedCartItem[] = (cartData.items || []).map(mapServiceCartItemToUICartItem);
      
      // 合并本地 properties 字段，使用产品类型+料号作为复合key
      extendedItems = extendedItems.map(item => {
        if (item.part_number) {
          const cacheKey = `${item.product_type}_${item.part_number}`;
          const localProps = cartPropertiesMap.current[cacheKey];
          if (localProps) {
            // 只用非空值合并 properties
            item.properties = mergeNoEmpty(item.properties || {}, localProps || {});
            item.name = localProps.name || item.name;
            item.image_url = localProps.image_url || item.image_url;
          }
        }
        return item;
      });
      
      setItems(extendedItems);
      console.log('🛒 [CartContext.refreshCart] Cart refreshed with', extendedItems.length, 'items');
    } catch (error) {
      console.error('🛒 [CartContext.refreshCart] Error refreshing cart:', error);
    }
  };
  
  // 修复购物车显示问题
  const clearInvalidItems = async () => {
    console.log('🔧 [CartContext.clearInvalidItems] Starting cart display fixes');
    try {
      setLoading(true);
      
      // 找出显示有问题的商品
      const problemItems = items.filter(item => {
        // 检查是否缺少必要的显示信息
        const hasDisplayIssues = !item.name || item.name === 'Invalid Type' || 
                                !item.image_url || item.image_url.includes('placeholder');
        return hasDisplayIssues;
      });
      
      if (problemItems.length === 0) {
        console.log('✅ [CartContext.clearInvalidItems] No display issues found');
        setSyncError('购物车显示正常，无需修复');
        setTimeout(() => setSyncError(null), 2000);
        return;
      }
      
      console.log(`🔧 [CartContext.clearInvalidItems] Found ${problemItems.length} items with display issues:`, 
        problemItems.map(item => ({ 
          part_number: item.part_number, 
          name: item.name, 
          product_type: item.product_type 
        }))
      );
      
      // 重新获取购物车数据以修复显示
      await fetchCart();
      
      setSyncError(`已修复 ${problemItems.length} 个商品的显示问题`);
      setTimeout(() => setSyncError(null), 3000);
      
    } catch (error) {
      console.error('❌ [CartContext.clearInvalidItems] Error fixing cart display:', error);
      setSyncError('修复购物车显示时出错');
    } finally {
      setLoading(false);
    }
  };
  
  const contextValue: CartContextType = {
    items,
    addItem,
    removeItem,
    removeMultipleItems,
    updateQuantity,
    clearCart,
    clearInvalidItems,
    totalPrice,
    itemCount,
    selectedItems,
    selectedCount,
    selectedTotal,
    toggleItemSelection,
    selectAll,
    isItemSelected,
    loading,
    syncError
  };
  
  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

// 使用购物车上下文的自定义钩子
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 